import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SupportAccessService } from '../services/support-access.service';
import { normalizeRole } from '../../shared/utils/role.utils';
import {
  findFirstAccessibleSupportPage,
  findSupportPageAccessOptionByRoute,
  SUPPORT_DEFAULT_ALLOWED_ROUTES
} from '../utils/support-page-access.util';

export const supportPageAccessGuard: CanActivateChildFn = async (childRoute, state) => {
  const authService = inject(AuthService);
  const supportAccessService = inject(SupportAccessService);
  const router = inject(Router);
  const currentUser = authService.getCurrentUser();

  if (!currentUser) {
    return router.createUrlTree(['/login']);
  }

  if (normalizeRole(currentUser.role) !== 'SUPPORT') {
    return true;
  }

  const routePath = state.url.split('?')[0];
  if (SUPPORT_DEFAULT_ALLOWED_ROUTES.has(routePath)) {
    return true;
  }

  try {
    const supportAccess = await firstValueFrom(supportAccessService.getSupportAccess());
    const selectedPermissionCodes = new Set(supportAccess?.permissionCodes ?? []);
    const matchedPage = findSupportPageAccessOptionByRoute(routePath, router.config);
    const hasAccess = matchedPage
      ? matchedPage.permissionCodes.some(code => selectedPermissionCodes.has(code))
      : false;

    if (hasAccess) {
      return true;
    }

    const firstAllowedPage = findFirstAccessibleSupportPage(
      supportAccess?.permissionCodes ?? [],
      router.config
    );

    if (firstAllowedPage?.route && firstAllowedPage.route !== routePath) {
      return router.createUrlTree([firstAllowedPage.route]);
    }

    return router.createUrlTree(['/support/login']);
  } catch (error) {
    console.error('Failed to verify support page access:', error);
    return router.createUrlTree(['/support/login']);
  }
};
